import React, { useState, useEffect } from 'react';
import { supabase, handleSupabaseError, OperationType } from '../supabase';
import { CryptoEvent, UserProfile, CATEGORY_BY_VALUE, rowToEvent } from '../types';
import { format } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  ExternalLink,
  ShieldCheck,
  Zap,
  ArrowUpRight,
} from 'lucide-react';

interface ClientDashboardProps {
  userProfile: UserProfile;
  onAddEvent: () => void;
  onSelectEvent: (event: CryptoEvent) => void;
}

const statusPill = (status: string) => {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#14f195] text-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider">
        <CheckCircle className="w-3 h-3" />
        Approved
      </span>
    );
  if (status === 'pending')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ffd24a] text-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ff6a6a] text-white text-[10px] font-bold uppercase tracking-wider">
        <XCircle className="w-3 h-3" />
        Rejected
      </span>
    );
  return null;
};

export const ClientDashboard: React.FC<ClientDashboardProps> = ({
  userProfile,
  onAddEvent,
  onSelectEvent,
}) => {
  const [myEvents, setMyEvents] = useState<CryptoEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    const fetchMine = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('created_by', authUser.id)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        handleSupabaseError(error, OperationType.SELECT, 'events');
        setLoading(false);
        return;
      }
      setMyEvents((data ?? []).map(rowToEvent));
      setLoading(false);

      // realtime
      if (!channel) {
        channel = supabase
          .channel(`events:client:${authUser.id}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'events', filter: `created_by=eq.${authUser.id}` },
            () => void fetchMine(),
          )
          .subscribe();
      }
    };

    void fetchMine();
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-[#9945ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const trusted = userProfile.tier === 'trusted';

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      <div className="sticker bg-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
              Welcome back
            </p>
            <h2 className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-[#0b0b0c]">
              {userProfile.displayName} submissions
            </h2>
            <p className="text-sm text-[#0b0b0c]/65 mt-1">
              Track and manage your submitted events as <strong>{userProfile.companyName}</strong>.
            </p>
          </div>
          <button
            onClick={onAddEvent}
            className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add event
          </button>
        </div>
      </div>

      <div
        className="sticker p-4 flex items-start gap-3"
        style={{ background: trusted ? '#14f195' : '#ffd24a' }}
      >
        <div className="w-9 h-9 sticker-soft bg-white flex items-center justify-center shrink-0">
          {trusted ? <Zap className="w-4 h-4 text-[#0b0b0c]" /> : <ShieldCheck className="w-4 h-4 text-[#0b0b0c]" />}
        </div>
        <div>
          <p className="text-sm font-bold text-[#0b0b0c]">
            {trusted ? 'Trusted partner' : 'Standard client'}
          </p>
          <p className="text-[12px] text-[#0b0b0c]/75">
            {trusted
              ? 'Your events are published automatically to the calendar.'
              : 'Your events require admin approval before appearing on the calendar.'}
          </p>
        </div>
      </div>

      <div className="grid gap-3">
        {myEvents.length === 0 ? (
          <div className="sticker bg-[#e9dffa] text-center py-16">
            <div className="w-16 h-16 sticker-soft bg-white inline-flex items-center justify-center mb-4">
              <CalendarIcon className="w-7 h-7 text-[#0b0b0c]/55" />
            </div>
            <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
              No events yet
            </h3>
            <p className="text-sm text-[#0b0b0c]/65 mb-5">You haven't submitted any events.</p>
            <button
              onClick={onAddEvent}
              className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              Submit your first event
            </button>
          </div>
        ) : (
          myEvents.map((event) => {
            const cat = CATEGORY_BY_VALUE[event.category];
            return (
              <div
                key={event.id}
                className="sticker bg-white p-4 flex flex-col gap-3 group"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {cat && (
                      <span
                        className="px-2 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
                        style={{
                          backgroundColor: cat.fill,
                          color: cat.textColor === 'white' ? '#fff' : '#0b0b0c',
                        }}
                      >
                        {cat.label}
                      </span>
                    )}
                    {event.chain && (
                      <span className="px-2 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
                        {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
                      </span>
                    )}
                  </div>
                  {statusPill(event.status)}
                </div>

                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-bold text-lg uppercase tracking-tight text-[#0b0b0c]">
                        {event.title}
                      </h3>
                      {event.link && (
                        <a
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center text-[#0b0b0c]/55 hover:text-[#9945ff] transition-colors shrink-0"
                          aria-label={`Open ${event.title} link`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-[#0b0b0c]/65">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(event.date, 'MMM d, yyyy')}
                        {event.time && ` · ${event.time}`}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onSelectEvent(event)}
                    className="w-11 h-11 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-white group-hover:bg-[#0b0b0c] group-hover:text-white inline-flex items-center justify-center shrink-0 transition-colors"
                    aria-label={`View ${event.title}`}
                  >
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
